import { confirmDialog } from '../shared/rc-confirm-dialog';
import { type BillingOverviewDto, billingApi } from '../../services/billing.api';
import { type DocumentDto, type PropertyDto, type TenantDto, documentsApi, propertiesApi, tenantsApi } from '../../services/api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { openDocumentShareModal, openDocumentUploadDialog } from './document-dialogs';
import './rc-document-list-view';
import { RcDocumentListView } from './rc-document-list-view';

type DocumentEventDetail = {
  id?: string;
  filterType?: string;
};

const DOCUMENT_EVENTS = ['add', 'download', 'share', 'delete', 'filter-change'] as const;

export class RcDocumentList extends HTMLElement {
  private documents: DocumentDto[] = [];
  private properties: PropertyDto[] = [];
  private tenants: TenantDto[] = [];
  private billingOverview: BillingOverviewDto | null = null;
  private filterType = '';
  private disposeLocaleChange?: () => void;

  async connectedCallback(): Promise<void> {
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    DOCUMENT_EVENTS.forEach((eventName) => this.addEventListener(`document-${eventName}`, this.handleDocumentEvent as EventListener));
    await this.loadData();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    DOCUMENT_EVENTS.forEach((eventName) => this.removeEventListener(`document-${eventName}`, this.handleDocumentEvent as EventListener));
  }

  private async loadData(): Promise<void> {
    try {
      const [documents, properties, tenants, billingOverview] = await Promise.all([
        documentsApi.getAll(),
        propertiesApi.getAll(),
        tenantsApi.getAll(),
        billingApi.getOverview().catch(() => null),
      ]);
      this.documents = documents;
      this.properties = properties;
      this.tenants = tenants;
      this.billingOverview = billingOverview;
      this.render();
    } catch (error) {
      console.error('Failed to load documents:', error);
      this.innerHTML = `<div class="empty-state">${t('document.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-document-list-view></rc-document-list-view>';
    const view = this.querySelector('rc-document-list-view') as RcDocumentListView | null;
    if (!view) return;
    view.documents = this.documents;
    view.filterType = this.filterType;
    view.billingOverview = this.billingOverview;
    view.render();
  }

  private handleDocumentEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<DocumentEventDetail>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'document-add':
        openDocumentUploadDialog({
          properties: this.properties,
          tenants: this.tenants,
          billingOverview: this.billingOverview,
          onUploaded: async () => this.loadData(),
        });
        return;
      case 'document-download':
        if (detail.id) await this.handleDownload(detail.id);
        return;
      case 'document-share':
        if (detail.id) await this.handleShare(detail.id);
        return;
      case 'document-delete':
        if (detail.id) await this.handleDelete(detail.id);
        return;
      case 'document-filter-change':
        this.filterType = detail.filterType || '';
        this.render();
        return;
      default:
        return;
    }
  };

  private async handleDownload(id: string): Promise<void> {
    try {
      const document = this.documents.find((item) => item.id === id);
      await documentsApi.download(id, document?.fileName);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  }

  private async handleShare(id: string): Promise<void> {
    try {
      const result = await documentsApi.createShareLink(id);
      await this.copyText(result.url);
      openDocumentShareModal(result.url, result.expiresAt, async () => this.copyText(result.url));
      toastService.success(t('document.shareCopied'));
    } catch (error) {
      console.error('Failed to create share link:', error);
      toastService.error(t('document.shareError'));
    }
  }

  private async copyText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const input = document.createElement('input');
    input.value = text;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  }

  private async handleDelete(id: string): Promise<void> {
    if (!await confirmDialog(t('document.confirmDelete'))) return;
    try {
      await documentsApi.delete(id);
      await this.loadData();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  }
}

if (!customElements.get('rc-document-list')) {
  customElements.define('rc-document-list', RcDocumentList);
}
